import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useMemo } from "react";

import PassengerHistoryDialog from "@/components/PassengerHistoryDialog.tsx";
import { TaskStatuses } from "@/constants/status";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { useGetTasks } from "@/requests/transport.queries";
import { useTrackingDate, useTrackingFilters } from "@/stores/use-tracking-table";
import { IPlanPassenger, Task } from "@/types/tasks.types";

import TrackingBulkCancelDialog from "./components/TrackingBulkCancelUETDSDialog";
import TrackingBulkRegisterDialog from "./components/TrackingBulkRegisterDialog";
import TrackingPanel from "./components/TrackingPanel";
import TrackingUnassignTaskDialog from "./components/TrackingUnassignTaskDialog";
import UpdateTaskStatus from "./components/UpdateTaskStatus";
import MobileTrackingDashboard from "./components/mobile/MobileTrackingDashboard";
import TrackingTable from "./components/table";

const filterTasks = (
    tasks: Task<IPlanPassenger<"api">>[],
    { status, selectedDrivers, search }: { status: TaskStatuses | null; selectedDrivers: string[]; search: string }
) => {
    return tasks.filter((task) => {
        return (
            (!status || task.status === status) &&
            (!selectedDrivers.length || (task.driverId && selectedDrivers.includes(task.driverId))) &&
            (!search.length ||
                task.driverName.toLowerCase().includes(search) ||
                task.passengers.some(
                    (passenger) =>
                        passenger.name.toLowerCase().includes(search) ||
                        passenger.companions.some((companion) => companion.toLowerCase().includes(search))
                ))
        );
    });
};

function TrackingPage() {
    const trackingDate = useTrackingDate();
    const { data, isPending, isFetching, refetch } = useGetTasks({ date: trackingDate });
    const api = useGridApiRef();
    const allRows = useMemo(() => data || [], [data]);
    const filters = useTrackingFilters();
    const { size } = useWindowDimensions();

    const rows = useMemo(() => {
        return filterTasks(allRows, filters);
    }, [filters, allRows]);

    return (
        <TrackingPanel>
            {size.sm ? (
                <TrackingTable
                    apiRef={api}
                    loading={isPending || isFetching}
                    rows={rows}
                    allRows={allRows}
                    reloadTable={refetch}
                />
            ) : (
                <MobileTrackingDashboard rows={rows} loading={isPending || isFetching} />
            )}
            <UpdateTaskStatus />
            <TrackingBulkRegisterDialog />
            <TrackingBulkCancelDialog />
            <TrackingUnassignTaskDialog />
            <PassengerHistoryDialog />
        </TrackingPanel>
    );
}

export default TrackingPage;
